import { useEffect, useCallback, useRef } from 'react';
import { useAuthUser } from '../store';
import { useSocket } from '../context/SocketContext';
import { useToast } from '../components/ToastContext';
import type { ToastContextType } from '../components/ToastContext';
import socketService from '../services/unifiedSocketService';
import appointmentService from '../services/appointmentService';
import teleconsultService from '../services/UnifiedTeleconsultationService';
import type { CallEvent, SocketMessage } from '../services/unifiedSocketService';
import { SOCKET_EVENTS } from '../constants/socketEvents';
import { queryClient } from '../lib/queryClient';
import { logger } from '../utils/logger';

interface UseDashboardSyncOptions {
  onDataUpdate?: () => void;
  onVitalsUpdate?: (data: unknown) => void;
  enableRealtime?: boolean;
}

export const useDashboardSync = (options: UseDashboardSyncOptions = {}) => {
  const user = useAuthUser();
  const toastContext = useToast();
  const { onDataUpdate, onVitalsUpdate, enableRealtime = true } = options;

  // Refs pour éviter les closures obsolètes
  const showToastRef = useRef<ToastContextType['showToast'] | null>(null);
  const onDataUpdateRef = useRef<(() => void) | undefined>(undefined);
  const onVitalsUpdateRef = useRef<((data: unknown) => void) | undefined>(undefined);

  const safeShowToast = useCallback(
    (
      message: string,
      type?: Parameters<NonNullable<ToastContextType['showToast']>>[1],
      duration?: number,
      toastOptions?: Parameters<NonNullable<ToastContextType['showToast']>>[3]
    ) => {
      showToastRef.current?.(message, type, duration, toastOptions);
    },
    []
  );

  useEffect(() => {
    onDataUpdateRef.current = onDataUpdate;
  }, [onDataUpdate]);

  useEffect(() => {
    showToastRef.current = toastContext?.showToast;
  }, [toastContext]);

  useEffect(() => {
    onVitalsUpdateRef.current = onVitalsUpdate;
  }, [onVitalsUpdate]);

  // Actions synchronisées
  const confirmAppointment = useCallback(
    async (appointmentId: string) => {
      try {
        await appointmentService.confirmAppointment(appointmentId);
        socketService.emit('appointment:action', { action: 'confirmed', appointmentId, userId: user?.id });
        safeShowToast('Rendez-vous confirmé avec succès', 'success');
        onDataUpdateRef.current?.();
        return true;
      } catch {
        safeShowToast('Erreur lors de la confirmation', 'error');
        return false;
      }
    },
    [safeShowToast, user?.id]
  );

  const cancelAppointment = useCallback(
    async (appointmentId: string, reason?: string) => {
      try {
        await appointmentService.cancelAppointment(appointmentId, reason);
        socketService.emit('appointment:action', { action: 'cancelled', appointmentId, reason, userId: user?.id });
        safeShowToast('Rendez-vous annulé', 'success');
        onDataUpdateRef.current?.();
        return true;
      } catch {
        safeShowToast("Erreur lors de l'annulation", 'error');
        return false;
      }
    },
    [safeShowToast, user?.id]
  );

  const callNextPatient = useCallback(
    async (teleconsultationId?: string) => {
      try {
        if (teleconsultationId) {
          await teleconsultService.acceptNextPatient(teleconsultationId);
        } else {
          await teleconsultService.acceptNextPatient();
        }
        socketService.emit('waiting_room:action', { action: 'call_next', teleconsultationId, doctorId: user?.id });
        safeShowToast('Patient appelé avec succès', 'success');
        onDataUpdateRef.current?.();
        return true;
      } catch {
        safeShowToast("Erreur lors de l'appel", 'error');
        return false;
      }
    },
    [safeShowToast, user?.id]
  );

  const updatePatientPriority = useCallback(
    async (teleconsultationId: string, priority: 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT') => {
      try {
        socketService.updatePatientPriority(teleconsultationId, priority);
        safeShowToast('Priorité mise à jour', 'success');
        onDataUpdateRef.current?.();
        return true;
      } catch {
        safeShowToast('Erreur mise à jour priorité', 'error');
        return false;
      }
    },
    [safeShowToast]
  );

  // Initialisation des listeners Sockets
  useEffect(() => {
    if (!enableRealtime || !user?.id) return;

    let appointmentUpdateTimeout: NodeJS.Timeout | null = null;
    const handleAppointmentUpdate = () => {
      if (appointmentUpdateTimeout) clearTimeout(appointmentUpdateTimeout);
      appointmentUpdateTimeout = setTimeout(() => {
        logger.debug('Mise à jour rendez-vous dashboard via hook');
        queryClient.invalidateQueries({ queryKey: ['appointments'] });
        onDataUpdateRef.current?.();
      }, 1000);
    };

    const handleWaitingRoomUpdate = (...args: unknown[]) => {
      const data = args[0] as Record<string, unknown> | Array<unknown>;
      const isNewPatient = 
        !Array.isArray(data) && 
        (data?.type === 'patient_added' || (data?.patientId && data?.patientId !== undefined)); 

      if (isNewPatient && user.role.toLowerCase() === 'doctor') {
        const patientName = (!Array.isArray(data) && (data as Record<string, unknown>)?.patientName as string) || 'Un nouveau patient';
        safeShowToast(`${patientName} vient d'arriver en salle d'attente !`, 'success', 5000, { sound: true });
      }
      onDataUpdateRef.current?.();
    };

    const handleNewMessage = (...args: unknown[]) => {
      const message = args[0] as SocketMessage;
      showToastRef.current?.(`Nouveau message de ${message.sender?.name}`, 'info');
      onDataUpdateRef.current?.();
    };

    const handleMessageRead = () => {
      onDataUpdateRef.current?.();
    };

    const handleIncomingCall = (call: CallEvent) => {
      if (call.status === 'ringing') {
        showToastRef.current?.(`Appel entrant de ${call.callerName || 'Inconnu'}`, 'info');
      }
    };

    const handleVitalsUpdate = (data: unknown) => {
      if (onVitalsUpdateRef.current) {
        onVitalsUpdateRef.current(data);
      } else {
        onDataUpdateRef.current?.();
      }
    };

    const handleReconnect = () => {
      onDataUpdateRef.current?.();
    };

    // Attach listeners
    socketService.onMessage(handleNewMessage);
    socketService.on(SOCKET_EVENTS.MESSAGE_READ, handleMessageRead);
    socketService.onWaitingRoomUpdate(handleWaitingRoomUpdate);
    socketService.onCallEvent((...args) => handleIncomingCall(args[0] as CallEvent));
    socketService.onVitalsUpdate(handleVitalsUpdate);

    const appointmentEvents = [
      'appointment:update', 'appointment:created', 'appointment:updated',
      'appointment:confirmed', 'appointment:cancelled', 'appointment:completed'
    ];
    appointmentEvents.forEach(evt => socketService.on(evt, handleAppointmentUpdate));

    if (typeof window !== 'undefined') {
      window.addEventListener('socket:reconnected', handleReconnect);
    }

    return () => {
      // Detach listeners on unmount
      socketService.off('message', handleNewMessage as unknown as (data: unknown) => void);
      socketService.off(SOCKET_EVENTS.MESSAGE_READ, handleMessageRead);
      socketService.off('waiting_room', handleWaitingRoomUpdate as unknown as (data: unknown) => void);
      socketService.off('call_event', handleIncomingCall as unknown as (data: unknown) => void);
      socketService.off('vitals_update', handleVitalsUpdate as unknown as (data: unknown) => void);
      appointmentEvents.forEach(evt => socketService.off(evt, handleAppointmentUpdate));
      
      if (typeof window !== 'undefined') {
        window.removeEventListener('socket:reconnected', handleReconnect);
      }
      if (appointmentUpdateTimeout) clearTimeout(appointmentUpdateTimeout);
    };
  }, [user?.id, user?.role, enableRealtime, safeShowToast]);

  const { isConnected: socketIsConnected } = useSocket();

  return {
    confirmAppointment,
    cancelAppointment,
    callNextPatient,
    updatePatientPriority,
    isConnected: socketIsConnected,
  };
};
